import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, ArrowRight } from "lucide-react";

interface PendingMatchesAlertProps {
  approvableCount: number;
  onViewPending: () => void;
}

export function PendingMatchesAlert({ approvableCount, onViewPending }: PendingMatchesAlertProps) {
  const [open, setOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    if (approvableCount > 0 && !hasShown) {
      const timer = setTimeout(() => {
        setOpen(true);
        setHasShown(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    return;
  }, [approvableCount, hasShown]);

  const handleViewPending = () => {
    setOpen(false);
    onViewPending();
  };

  const handleDismiss = () => {
    setOpen(false);
  };

  if (approvableCount === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm mx-auto" data-testid="dialog-pending-matches-alert">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Clock className="h-8 w-8 text-orange-500" />
          </div>
          <DialogTitle className="text-xl">
            {approvableCount === 1 
              ? "Tienes un partido por confirmar" 
              : `Tienes ${approvableCount} partidos por confirmar`
            }
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {approvableCount === 1 
              ? "Un jugador ha registrado un partido contigo. Revisa el resultado y confírmalo."
              : "Otros jugadores han registrado partidos contigo. Revisa los resultados y confírmalos."
            }
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col gap-2 sm:flex-col pt-4">
          <Button 
            onClick={handleViewPending}
            className="w-full bg-orange-600 hover:bg-orange-700"
            data-testid="button-view-pending"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Ver partidos pendientes
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleDismiss}
            className="w-full"
            data-testid="button-dismiss-pending"
          >
            Más tarde
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
